import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { v4 as uuid } from "uuid";
import * as XLSX from "xlsx";
import agent from "../api/agent";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, Button, Checkbox, Chip, FormControlLabel, FormHelperText, FormLabel, Modal, Radio, RadioGroup, Switch, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import AdminLayout from "../layout/AdminLayout";
import { basename, env } from "../utils/env";
import { PrintEntry } from "../models/printEntry";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from '@mui/icons-material/Upload';
import SearchIcon from '@mui/icons-material/Search';
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { PrintGift } from "../models/printGift";

export default function ListPrintEntries() {
  const [printEntries, setPrintEntries] = useState<PrintEntry[]>([]);
  const [printGifts, setPrintGifts] = useState<PrintGift[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const location = useLocation();

  const initialFormValues: any = {
    name: "",
    datePicked: dayjs(),
    design: null,
    receivingCode: null,
    hasPicked: false,
    hasCollected: false,
    editType: 1,
    printGiftId: null,
    printGift: null
  };
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [errors, setErrors] = useState<any>({});
  const [fileInputKey, setFileInputKey] = useState(0);
  const [viewEntry, setViewEntry] = useState<PrintEntry | null>(null);
  const [openViewModal, setOpenViewModal] = useState(false);

  const fetchPrintEntries = async () => {
    setLoading(true);
    try {
      const response = await agent.PrintEntries.list();
      const fetchedPrintEntries = response;
      setPrintEntries(fetchedPrintEntries);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Baskı hediye listesi getirilemedi.');
    }
  };

  useEffect(() => {
    fetchPrintEntries();
    agent.PrintEntries.listPrintGifts().then(setPrintGifts);
  }, []);

  useEffect(() => {
    if (location.state?.reload) {
      fetchPrintEntries();
    }
  }, [location.state]);

  const designImages = Array.from({ length: 6 }, (_, i) => `${process.env.PUBLIC_URL}/assets/design/design-${i + 1}.png`);

  const handleDeletePrintEntry = (id: string) => {
    Swal.fire({
        title: 'Silmek istediğinizden emin misiniz?',
        text: 'Silme işlemini geri alamazsınız!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Tamam',
        cancelButtonText: 'İptal',
    }).then((result) => {
        if (result.isConfirmed) {
            agent.PrintEntries.delete(id).then(() => {
                setPrintEntries([...printEntries.filter(x => x.id !== id)]);
                toast.success('Baskı hediye girdisi silindi!');
                setPrintEntries([...printEntries.filter(x => x.id !== id)])
            }).catch((error) => {
                toast.error('Baskı hediye girdisi silinirken bir hata oluştu.');
            });
        }
    });
  };

  const handleDeleteAllPrintEntries = () => {
    Swal.fire({
      title: "Silmek istediğinizden emin misiniz?",
      text: "Baskı hediye listesi için kaydedilmiş tüm girdi verileri silinecektir. Silme işlemini geri alamazsınız!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Tamam",
      cancelButtonText: "İptal",
    }).then((result) => {
      if (result.isConfirmed) {
        agent.PrintEntries.deleteAll()
          .then(() => {
            setPrintEntries([]);
            toast.success("Tüm Baskı hediye girdileri silindi!");
          })
          .catch((error) => {
            toast.error("Veriler silinirken bir hata oluştu.");
          });
      }
    });
  };

  const handleOnExport = () => {
    const filteredData = printEntries.map((entry) => {
      const printEntryData: any = {
        name: entry.name,
        datePicked: entry.datePicked ? dayjs(entry.datePicked).add(3, 'hour').format("DD.MM.YYYY HH:mm") : "",
        receivingCode: entry.receivingCode,
        printGiftId: printGifts.find((gift) => gift.id === entry.printGiftId)?.name || "",
        design: entry.design ? `Tasarım ${entry.design}` : ""
      };
      return printEntryData;
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const currentDate = dayjs().format("DD-MM-YYYY");

    const headers = ["Çalışan Adı", "Hediye Seçim Tarihi", "Hediye Teslim Kodu", "Hediye", "Tasarım"];

    headers.forEach((header, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      worksheet[cellRef] = { t: "s", v: header, s: { font: { bold: true } } };
    });

    const columnWidths = headers.map(() => ({ wch: 20 }));;
    worksheet["!cols"] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, "HediyeBaski");

    XLSX.writeFile(
      workbook,
      `birbank-${currentDate}-hediye-baski-listesi.xlsx`
    );
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const rows: (string | undefined)[][] = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
      });
  
      // Transform data into participants
      const printEntries = rows.filter((row) => row[0]).map((row) => ({
        id: uuid(),
        name: row[0] || ""
      }));
  
      if (printEntries.length === 0) {
        toast.error(
          "Excel dosyasında Katılımcı Adı alanı olan ilk kolon boş bırakılamaz!"
        );
      } else {
        uploadPrintEntries(printEntries);
      }
      setFileInputKey(prevKey => prevKey + 1);
    };
    reader.readAsArrayBuffer(file);
  };

  const uploadPrintEntries = async (printEntries: PrintEntry[]) => {
    try {
      await agent.PrintEntries.bulkCreate(printEntries);
      setPrintEntries((prev) => [...prev, ...printEntries]);
      toast.success("Baskı hediye girdileri başarıyla eklendi");
    } catch (error) {
      toast.error("Baskı hediye girdileri eklenirken bir hata oluştu");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: name === 'design' ? parseInt(value) : value
    });
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormValues({ ...formValues, [field]: value });
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    handleFieldChange("datePicked", newValue || dayjs())
  };

  const validateForm = () => {
    let tempErrors: any = {};

    if (!formValues.name) tempErrors.name = "Çalışan Adı zorunludur";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>)  => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    const printEntryData = {
      id: uuid(),
      name: formValues.name
    }

    try {
      await agent.PrintEntries.create(printEntryData);
      setPrintEntries([...printEntries, printEntryData]);
      toast.success("Baskı hediye girdisi başarıyla eklendi");
    } catch (error) {
      toast.error("Baskı hediye girdileri eklenirken bir hata oluştu");
    }

    handleCreateClose();
  };

  const handleCreateOpen = () => {
    setFormValues(initialFormValues); // Ensure reset
    setOpenCreateModal(true);
  };

  const handleCreateClose = () => {    
    setOpenCreateModal(false);
    setFormValues(initialFormValues);
  };

  const handleEdit = async (e: React.FormEvent<HTMLFormElement>)  => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    const printEntryData = {
      id: formValues.id,
      name: formValues.name,
      datePicked: (formValues.datePicked as Dayjs).toISOString(),
      design: formValues.design,
      receivingCode: formValues.receivingCode,
      hasPicked: formValues.hasPicked,
      hasCollected: formValues.hasCollected,
      editType: 2,
      printGiftId: formValues.printGiftId,
      printGift: formValues.printGift
    }

    try {
      await agent.PrintEntries.update(printEntryData);
      setPrintEntries([...printEntries, printEntryData]);
      toast.success("Baskı hediye girdisi başarıyla düzenlendi");
    } catch (error) {
      toast.error("Baskı hediye girdileri düzenlenirken bir hata oluştu");
    }

    handleEditClose();
  };

  const handleEditClose = () => {    
    setOpenEditModal(false);
    setFormValues(initialFormValues);
  };

  const openEditModalWithData = async (id: string) => {
    try {
      const data = await agent.PrintEntries.details(id);
      setFormValues({
        id: data.id,
        name: data.name,
        datePicked: data.datePicked ? dayjs(data.datePicked) : null,
        design: data.design,
        receivingCode: data.receivingCode,
        hasPicked: data.hasPicked,
        hasCollected: data.hasCollected,
        printGiftId: data.printGiftId,
        printGift: data.printGift
      });
      setOpenEditModal(true);
    } catch (error) {
      toast.error("Girdi bilgisi alınırken hata oluştu");
    }
  };

  const handleSearchEntryByCode = async () => {
    const { value: inputCode } = await Swal.fire({
      title: "Teslim kodunu giriniz",
      input: "text",
      inputLabel: "5 Haneli Hediye Teslim Kodu",
      showCancelButton: true,
      confirmButtonText: "Ara",
      cancelButtonText: "İptal",
    });

    if (inputCode) {
      const found = printEntries.find((entry) => entry.receivingCode?.toLowerCase() === inputCode.toLowerCase());
      if (found) {
        setViewEntry(found);
        setOpenViewModal(true);
      } else {
        toast.error("Kod ile eşleşen bir kayıt bulunamadı.");
      }
    }
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "name", headerName: "Çalışan Adı", width: 160 },
    { field: "receivingCode", headerName: "Hediye Teslim Kodu", width: 160 },
    {
      field: "datePicked",
      headerName: "Hediye Seçim Tarihi",
      description: "Çalışanın hediyesini seçtiği tarih.",
      width: 180,
      renderCell: (params: any) => (
        params.value ? dayjs(params.value as string).add(3, 'hour').format("DD.MM.YYYY HH:mm") : <Chip label="Henüz seçmedi" color="default" />
      ),
    },
    {
      field: "hasPicked",
      headerName: "Hediye Seçti mi?",
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Seçti" : "Henüz seçmedi"}
          color={params.value ? "success" : "default"}
        />
      ),
    },
    {
      field: "hasCollected",
      headerName: "Hediyesini Teslim Aldı Mı?",
      width: 200,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Teslim aldı" : "Henüz almadı"}
          color={params.value ? "success" : "default"}
        />
      ),
    },
    { field: "printGiftId", headerName: "Hediye", width: 120 },
    { field: "design", headerName: "Tasarım", width: 120 },
    {
      field: "actions",
      headerName: "Eylemler",
      width: 360,
      renderCell: (params) => (
        <>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => openEditModalWithData(params.row.id)}
            style={{marginRight: "15px"}}
          >
            Düzenle
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleDeletePrintEntry(params.row.id)}
          >
            Sil
          </Button>
      </>
      ),
    },
  ];

  return (
    <>
      <AdminLayout title="Hediye Baskı Listesi">
        <Grid
          container
          spacing={2}
          marginBottom={2}
          sx={{ justifyContent: "space-between" }}
        >
          <Grid size={{xs: 6, sm: 6, md: 6}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateOpen}
              sx={{ mb: 2, mr: 3 }}
            >
              Liste Girdisi Ekle
            </Button>
            <Button
                variant="contained"
                color="warning"
                onClick={() => window.open(`${basename}/printGiftSelect/`, '_blank')}
                sx={{mb: 2, mr: 3}}
              >
              Hediye Seçim Ekranı
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteAllPrintEntries}
              sx={{ mb: 2 }}
            >
              Tüm Girdileri Sil
            </Button>
          </Grid>
          <Grid size={{xs: 6, sm: 6, md: 6}}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              color="success"
              onClick={handleOnExport}
              sx={{ mr: 3 }}
            >
              Excel Çıktı Al
            </Button>
            
            <Button
              variant="contained"
              color="info"
              startIcon={<SearchIcon/>}
              onClick={handleSearchEntryByCode}
              sx={{ mr: 3 }}
            >
              Teslim Koduyla Bilgileri Göster
            </Button>

            <input
              key={fileInputKey}
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              id="file-upload"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file);
              }}
            />
            <label htmlFor="file-upload">
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                color="success"
                component="span"
              >
                Excel Dosya Gönder
              </Button>
            </label>
          </Grid>
        </Grid>
        
        <div className="gifts-container">
          {printGifts.map((gift) => (
            <div className="gift-box">
              <img src={env + gift.giftImage} alt={gift.name} className="gift-image" />
              <h3>{gift.name}</h3>
              <p>Kalan stok: {gift.stock}</p>
            </div>
          ))}
        </div>
        <DataGrid
          rows={printEntries}
          columns={columns}
          loading={loading}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 50 },
            },
          }}
          pageSizeOptions={[50, 100]}
          checkboxSelection
        />
        <Modal
          open={openCreateModal}
          onClose={() => setOpenCreateModal(false)}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          className="game-modal modal"
        >
          <Box
            sx={{
              width: 400,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              outline: "none",
            }}
          >
            <p>Baskı Hediye Girdisi Ekle</p>
            <form className="login-form" onSubmit={handleCreate}>
              <TextField
                label="Çalışan Adı"
                name="name"
                type="text"
                fullWidth
                variant="outlined"
                value={formValues.name}
                onChange={handleInputChange}
                required
                helperText={errors.name}
                error={!!errors.name}
              />
              <Button
                variant="contained"
                type="submit"
                sx={{ my: 4, backgroundColor: "#FAB406" }}
              >
                Gönder
              </Button>
            </form>
          </Box>
        </Modal>
        <Modal
          open={openEditModal}
          onClose={() => setOpenEditModal(false)}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          className="game-modal modal"
        >
          <Box
            sx={{
              width: 850,
              maxWidth: "90vw",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              outline: "none",
            }}
          >
            <p>Baskı Hediye Girdisini Düzenle</p>
            <form className="login-form" onSubmit={handleEdit}>
              <TextField
                label="Çalışan Adı"
                name="name"
                type="text"
                fullWidth
                variant="outlined"
                value={formValues.name}
                onChange={handleInputChange}
                required
                helperText={errors.name}
                error={!!errors.name}
              />
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                <DateTimePicker
                  label="Hediye Seçim Tarihi"
                  value={formValues.datePicked || null}
                  onChange={handleDateChange}
                  slotProps={{textField: {error: !!errors.datePicked, helperText: errors.datePicked ? errors.datePicked : ""}}}
                />
              </LocalizationProvider>
              <TextField
                label="Hediye Teslim Kodu"
                name="receivingCode"
                type="text"
                fullWidth
                variant="outlined"
                value={formValues.receivingCode}
                onChange={handleInputChange}
              />
              <Grid
                container
                spacing={1}
                marginBottom={2}
                sx={{ justifyContent: "flex-start" }}
              >
                <Grid size={{xs: 12, sm: 12, md: 12}} sx={{textAlign: "left"}}>
                  <FormLabel>
                    <Checkbox
                      checked={formValues.hasPicked}
                      onChange={(e) =>
                        setFormValues({ ...formValues, hasPicked: e.target.checked })
                      }
                    />
                    Hediye seçti mi?
                  </FormLabel>
                </Grid>
                
              </Grid>

              <Grid
                container
                spacing={1}
                marginBottom={2}
                sx={{ justifyContent: "flex-start" }}
              >
                <Grid size={{xs: 12, sm: 12, md: 12}} sx={{textAlign: "left"}}>
                  <FormLabel>
                    <Checkbox
                      checked={formValues.hasCollected}
                      onChange={(e) =>
                        setFormValues({ ...formValues, hasCollected: e.target.checked })
                      }
                    />
                    Teslim aldı mı?
                  </FormLabel>
                </Grid>                
              </Grid>

              <RadioGroup
                row
                value={formValues.printGiftId}
                onChange={handleInputChange}
                sx={{display: "flex", gap: "20px", marginBottom: "40px"}}
              >
                {printGifts.map((gift) => (
                  <FormControlLabel
                    key={gift.id}
                    value={gift.id}
                    control={<Radio disabled={gift.stock === 0} />}
                    disabled={gift.stock === 0}
                    label={
                      <div className={`gift-radio ${formValues.printGiftId === gift.id ? "selected" : ""} ${gift.stock === 0 ? "disabled" : ""}`}>
                        <div className="gift-box">
                          <img src={env + gift.giftImage} alt={gift.name} className="gift-image" />
                        </div>
                        <div>{gift.name}</div>
                        {gift.stock === 0 && <small className="stock-message">Stokda yoxdur</small>}
                      </div>
                    }
                  />
                ))}
              </RadioGroup>

              <Typography variant="subtitle2" sx={{ textAlign: "left" }}>
                Tasarım
              </Typography>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="design"
                value={formValues.design || 0}
                onChange={handleInputChange}
              >
                {designImages.map((src, index) => (
                  <div className="design-box" key={index}>
                    <FormControlLabel
                      key={index}
                      value={index + 1}
                      control={<Radio />}
                      label={
                        <div className={`design-radio ${formValues.design === index + 1 ? "selected" : ""}`}>
                          <img src={src} alt={`Design ${index + 1}`} className="design-image" />
                        </div>
                      }
                    />
                  </div>
                ))}
              </RadioGroup>
              <Button
                variant="contained"
                type="submit"
                sx={{ my: 4, backgroundColor: "#FAB406" }}
              >
                Gönder
              </Button>
            </form>
          </Box>
        </Modal>

        <Modal
          open={openViewModal}
          onClose={() => setOpenViewModal(false)}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          className="game-modal modal"
        >
          <Box
            sx={{
              width: 800,
              maxWidth: "90vw",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              outline: "none",
            }}
          >
            {viewEntry && (
              <>
                <Typography variant="h3" sx={{ my: 4 }}>{viewEntry.name}</Typography>
                <small>Teslim Kodu</small>
                <Typography variant="h3" sx={{ mb: 3 }}>
                  {viewEntry.receivingCode}
                </Typography>

                <Grid
                  container
                  spacing={5}
                  sx={{
                    justifyContent: 'center',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: '10px',
                      flexDirection: 'column',
                      height: 'auto',
                      background: 'none',
                      width: '120px',
                      marginBottom: '20px'
                    }}
                  >
                    <small>Hediye Seçti mi?</small>
                    <Chip
                      label={viewEntry.hasPicked ? "Seçti" : "Henüz seçmedi"}
                      color={viewEntry.hasPicked ? "success" : "default"}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '10px',
                      flexDirection: 'column',
                      height: 'auto',
                      background: 'none',
                      width: '120px',
                      marginBottom: '20px'
                    }}
                  >
                    <small>Teslim Aldı mı?</small>
                    <Chip
                      label={viewEntry.hasCollected ? "Teslim aldı" : "Henüz almadı"}
                      color={viewEntry.hasCollected ? "success" : "default"}
                    />
                  </div>
                </Grid>

                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Hediye</Typography>
                    {printGifts.filter((gift) => gift.id === viewEntry.printGiftId).map((gift) => (
                      <>
                        <div key={gift.id} className="gift-box">
                          <img
                            className="gift-image large"
                            src={env + gift.giftImage}
                            alt={gift.name}
                            style={{ width: "100%", maxHeight: "240px", objectFit: "contain" }}
                          />
                        </div>
                        <Typography>{gift.name}</Typography>
                      </>
                    ))}
                  </Grid>

                  <Grid size={6}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Tasarım</Typography>
                    {viewEntry.design && (
                      <img
                        src={`${process.env.PUBLIC_URL}/assets/design/design-${viewEntry.design}.png`}
                        alt="Design"
                        style={{ width: "100%", maxHeight: "240px", objectFit: "contain" }}
                      />
                    )}
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </Modal>

      </AdminLayout>
    </>
  );
}